import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as usersApi from '@app/api/users.api';
import { User, CreateUserDto, UpdateUserDto, ChangePasswordDto, PaginationParams } from '@app/types/api.types';

export interface UsersState {
  items: User[];
  currentItem: User | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  loading: false,
  error: null,
};

export const fetchUsers = createAsyncThunk('users/fetchUsers', async (params: PaginationParams = { page: 1, limit: 10 }, { rejectWithValue }) => {
  try {
    return await usersApi.getUsers(params);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch users');
  }
});

export const fetchUserById = createAsyncThunk('users/fetchUserById', async (id: string, { rejectWithValue }) => {
  try {
    return await usersApi.getUserById(id);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch user');
  }
});

export const createUser = createAsyncThunk('users/createUser', async (data: CreateUserDto, { rejectWithValue }) => {
  try {
    return await usersApi.createUser(data);
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create user');
  }
});

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async ({ id, data }: { id: string; data: UpdateUserDto }, { rejectWithValue }) => {
    try {
      return await usersApi.updateUser(id, data);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update user');
    }
  }
);

export const deleteUser = createAsyncThunk('users/deleteUser', async (id: string, { rejectWithValue }) => {
  try {
    await usersApi.deleteUser(id);
    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete user');
  }
});

export const changePassword = createAsyncThunk(
  'users/changePassword',
  async ({ id, data }: { id: string; data: ChangePasswordDto }, { rejectWithValue }) => {
    try {
      await usersApi.changePassword(id, data);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to change password');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      // For future filter support
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
          hasNext: action.payload.hasNext,
          hasPrev: action.payload.hasPrev,
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentItem?.id === action.payload.id) {
          state.currentItem = action.payload;
        }
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload);
      });
  },
});

export const { setFilters, clearCurrentItem, clearError } = usersSlice.actions;
export default usersSlice.reducer;

